/**
 * Scroll Detection Hook for Infinite Loading
 * Detects when user scrolls near top to load older messages
 * Preserves scroll position when prepending content
 */

import { useCallback, useEffect, useRef, useState } from 'react';

interface UseScrollToLoadMoreOptions {
  /** Distance from top in pixels to trigger load (default: 200) */
  threshold?: number;
  /** Whether loading more is enabled */
  enabled?: boolean;
  /** Callback when scroll reaches threshold */
  onLoadMore: () => void;
  /** Whether currently loading more data */
  isLoading?: boolean;
  /** Whether there's more data to load */
  hasMore?: boolean;
}

interface UseScrollToLoadMoreReturn {
  /** Ref to attach to scrollable container */
  scrollRef: React.RefObject<HTMLDivElement | null>;
  /** Call this before prepending content to save scroll position */
  saveScrollPosition: () => void;
  /** Call this after prepending content to restore scroll position */
  restoreScrollPosition: () => void;
  /** Whether currently near top of scroll */
  isNearTop: boolean;
  /** Scroll to bottom of container */
  scrollToBottom: (behavior?: ScrollBehavior) => void;
  /** Whether user is at bottom (for auto-scroll on new messages) */
  isAtBottom: boolean;
}

export const useScrollToLoadMore = ({
  threshold = 200,
  enabled = true,
  onLoadMore,
  isLoading = false,
  hasMore = true,
}: UseScrollToLoadMoreOptions): UseScrollToLoadMoreReturn => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollPositionRef = useRef<{
    scrollHeight: number;
    scrollTop: number;
  } | null>(null);
  const [isNearTop, setIsNearTop] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const loadTriggeredRef = useRef(false);

  // Save scroll position before content prepend
  const saveScrollPosition = useCallback(() => {
    if (scrollRef.current) {
      scrollPositionRef.current = {
        scrollHeight: scrollRef.current.scrollHeight,
        scrollTop: scrollRef.current.scrollTop,
      };
    }
  }, []);

  // Restore scroll position after content prepend
  const restoreScrollPosition = useCallback(() => {
    if (scrollRef.current && scrollPositionRef.current) {
      const newScrollHeight = scrollRef.current.scrollHeight;
      const heightDiff = newScrollHeight - scrollPositionRef.current.scrollHeight;

      // Adjust scroll position by the height difference
      scrollRef.current.scrollTop = scrollPositionRef.current.scrollTop + heightDiff;
      scrollPositionRef.current = null;
    }
  }, []);

  // Scroll to bottom of container
  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior,
      });
    }
  }, []);

  // Handle scroll events
  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;

    // Check if near top
    const nearTop = scrollTop <= threshold;
    setIsNearTop(nearTop);

    // Check if at bottom (with small tolerance)
    const atBottom = scrollHeight - scrollTop - clientHeight < 50;
    setIsAtBottom(atBottom);

    // Trigger load when near top, not already loading, has more, and enabled
    if (nearTop && enabled && hasMore && !isLoading && !loadTriggeredRef.current) {
      loadTriggeredRef.current = true;
      onLoadMore();
    }
  }, [threshold, enabled, hasMore, isLoading, onLoadMore]);

  // Reset load triggered flag when loading completes
  useEffect(() => {
    if (!isLoading) {
      loadTriggeredRef.current = false;
    }
  }, [isLoading]);

  // Attach scroll listener
  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;

    scrollElement.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      scrollElement.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  return {
    scrollRef,
    saveScrollPosition,
    restoreScrollPosition,
    isNearTop,
    scrollToBottom,
    isAtBottom,
  };
};

export default useScrollToLoadMore;
